import classnames from "classnames"
import PropTypes from "prop-types"
import React, { useState } from "react"
import { useQuery } from "react-query"

import Modal from "src/components/Modal"
import ReloadableWidget from "src/components/ReloadableWidget"

import { queryApi } from "src/utils/api/ApiMethods"

const SEVERITY_ICONS = {
  red: "icon-exclamation-circle",
  orange: "icon-warning",
  yellow: "icon-warning-outline",
  blue: "icon-info",
  gray: "icon-info",
}

const queryWeatherAdvisories = (marinaSlug) => {
  return queryApi(`/manage/${marinaSlug}/dashboard/weather_advisories`)
}

const WeatherAdvisoryItem = ({ alert, onMoreClick, remainingCount }) => {
  const severityColor = alert.color || "gray"
  const iconClass = SEVERITY_ICONS[severityColor] || SEVERITY_ICONS.gray

  // Use text colors instead of backgrounds for compact display
  const textColorClass =
    {
      red: "text-red-600",
      orange: "text-orange-600",
      yellow: "text-yellow-600",
      blue: "text-blue-600",
      gray: "text-gray-600",
    }[severityColor] || "text-gray-600"

  const hasDetails = alert.description || alert.instruction

  return (
    <div
      className={classnames("flex", {
        "cursor-pointer hover:opacity-75": hasDetails,
      })}
      onClick={hasDetails ? () => onMoreClick() : undefined}
    >
      <i
        className={classnames(
          "icon text-l mr-2 mt-1",
          iconClass,
          textColorClass
        )}
      />
      <div>
        <div className={classnames("font-semibold", textColorClass)}>
          {alert.event}
          {remainingCount > 0 && (
            <span className="ml-2 text-xxs font-normal text-gray-500">
              +{remainingCount} more
            </span>
          )}
        </div>
        <div className="w-48 text-xs">
          {alert.parsed_headline || alert.headline}
        </div>
      </div>
    </div>
  )
}

WeatherAdvisoryItem.propTypes = {
  alert: PropTypes.object.isRequired,
  onMoreClick: PropTypes.func.isRequired,
  remainingCount: PropTypes.number,
}

const renderAdvisories = (alerts, onMoreClick) => {
  if (alerts && alerts.length > 0) {
    const mostSevereAlert = alerts[0]
    const remainingCount = alerts.length - 1

    return (
      <WeatherAdvisoryItem
        alert={mostSevereAlert}
        onMoreClick={() => onMoreClick(alerts)}
        remainingCount={remainingCount}
      />
    )
  }
}

const WeatherAdvisories = ({ marinaSlug }) => {
  const [allAlerts, setAllAlerts] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { isLoading, isError, data } = useQuery(
    ["weatherAdvisories", marinaSlug],
    () => queryWeatherAdvisories(marinaSlug),
    {
      retry: false,
      refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
      refetchOnWindowFocus: false,
    }
  )

  const handleMoreClick = (alerts) => {
    setAllAlerts(alerts)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setAllAlerts([])
  }

  const renderSingleAlert = (alert) => {
    const severityColor = alert.color || "gray"
    const iconClass = SEVERITY_ICONS[severityColor] || SEVERITY_ICONS.gray
    const textColorClass =
      {
        red: "text-red-600",
        orange: "text-orange-600",
        yellow: "text-yellow-600",
        blue: "text-blue-600",
        gray: "text-gray-600",
      }[severityColor] || "text-gray-600"

    return (
      <div
        key={alert.id}
        className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0"
      >
        <div className="mb-4">
          <h3
            className={classnames("mb-2 text-lg font-semibold", textColorClass)}
          >
            <i
              className={classnames(
                "icon mr-2 text-xl",
                iconClass,
                textColorClass
              )}
            />
            {alert.event}
          </h3>
          <p className="mb-3 font-medium text-gray-700">{alert.headline}</p>
        </div>

        {(alert.parsed_description || alert.description) && (
          <div className="mb-4">
            <h4 className="mb-2 font-semibold text-gray-900">Description:</h4>
            {alert.parsed_description ? (
              <div className="space-y-3 leading-relaxed text-gray-700">
                {alert.parsed_description.what && (
                  <div>
                    <span className="font-medium text-gray-800">What:</span>
                    <span className="ml-2">
                      {alert.parsed_description.what}
                    </span>
                  </div>
                )}
                {alert.parsed_description.where && (
                  <div>
                    <span className="font-medium text-gray-800">Where:</span>
                    <span className="ml-2">
                      {alert.parsed_description.where}
                    </span>
                  </div>
                )}
                {alert.parsed_description.when && (
                  <div>
                    <span className="font-medium text-gray-800">When:</span>
                    <span className="ml-2">
                      {alert.parsed_description.when}
                    </span>
                  </div>
                )}
                {alert.parsed_description.impacts && (
                  <div>
                    <span className="font-medium text-gray-800">Impacts:</span>
                    <span className="ml-2">
                      {alert.parsed_description.impacts}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="leading-relaxed text-gray-700">
                {alert.description}
              </p>
            )}
          </div>
        )}

        {alert.instruction && (
          <div className="mb-4">
            <h4 className="mb-2 font-semibold text-gray-900">Instructions:</h4>
            <p className="leading-relaxed text-gray-700">{alert.instruction}</p>
          </div>
        )}

        <div className="bg-gray-50 rounded p-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            {alert.severity && (
              <div>
                <span className="font-medium text-gray-600">Severity:</span>
                <span className="ml-2">{alert.severity}</span>
              </div>
            )}
            {alert.urgency && (
              <div>
                <span className="font-medium text-gray-600">Urgency:</span>
                <span className="ml-2">{alert.urgency}</span>
              </div>
            )}
            {alert.certainty && (
              <div>
                <span className="font-medium text-gray-600">Certainty:</span>
                <span className="ml-2">{alert.certainty}</span>
              </div>
            )}
            {alert.expires && (
              <div>
                <span className="font-medium text-gray-600">Expires:</span>
                <span className="ml-2">
                  {new Date(alert.expires).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const renderAlertDetails = () => {
    if (!allAlerts || allAlerts.length === 0) return null

    return (
      <Modal isOpen={isModalOpen} onClose={handleCloseModal}>
        <Modal.Header title={`Weather Advisories (${allAlerts.length})`} />
        <Modal.Body>
          <div className="max-h-96 space-y-6 overflow-y-auto">
            {allAlerts.map((alert) => renderSingleAlert(alert))}
          </div>
        </Modal.Body>
      </Modal>
    )
  }

  return (
    <>
      <ReloadableWidget isLoading={isLoading} isError={isError}>
        {data && renderAdvisories(data.alerts, handleMoreClick)}
      </ReloadableWidget>
      {renderAlertDetails()}
    </>
  )
}

WeatherAdvisories.propTypes = {
  marinaSlug: PropTypes.string.isRequired,
}

export default WeatherAdvisories
